// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/ClassificationSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, theme } from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Safely render value that might be an object {uid, label} or a string
function renderValue(value) {
  if (!value) return null;
  if (typeof value === 'object' && value.label) return value.label;
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ClassificationSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const cl = data?.classification;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = cl && (cl.category || cl.subcategory || cl.type || cl.brand || cl.manufacturer || cl.line || cl.tags?.length > 0);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      collapsible="icon"
      defaultActiveKey={['classification']}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'classification',
          label: (
            <Flex align="center" gap={8}>
              <AppstoreOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('classification')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              {cl.category && (
                <Descriptions.Item label={translateX('category')}>
                  {renderValue(cl.category)}
                </Descriptions.Item>
              )}
              {cl.subcategory && (
                <Descriptions.Item label={translateX('subcategory')}>
                  {renderValue(cl.subcategory)}
                </Descriptions.Item>
              )}
              {cl.type && (
                <Descriptions.Item label={translateX('type')}>
                  {renderValue(cl.type)}
                </Descriptions.Item>
              )}
              {cl.brand && (
                <Descriptions.Item label={translateX('brand')}>
                  {renderValue(cl.brand)}
                </Descriptions.Item>
              )}
              {cl.manufacturer && (
                <Descriptions.Item label={translateX('manufacturer')}>
                  {renderValue(cl.manufacturer)}
                </Descriptions.Item>
              )}
              {cl.line && (
                <Descriptions.Item label={translateX('product_line')}>
                  {renderValue(cl.line)}
                </Descriptions.Item>
              )}
              {cl.tags?.length > 0 && (
                <Descriptions.Item label={translateX('tags')}>
                  <Flex wrap="wrap" gap={4}>
                    {cl.tags.map((tag, idx) => (
                      <Tag key={idx} color="blue">{renderValue(tag)}</Tag>
                    ))}
                  </Flex>
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ClassificationSection;
